import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface JobCardProps {
  job: Job;
  isAuthenticated?: boolean;
  onApplyClick?: (jobId: string) => void;
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  employmentType: string;
  postedDate: string;
  deadline: string;
  description: string;
  requirements: string[];
  benefits: string[];
  companyLogo: string;
  companyLogoAlt: string;
  isFeatured: boolean;
  isUrgent: boolean;
  applicationUrl?: string;
  contactEmail?: string;
  experienceLevel: string;
}

const JobCard = ({ job, isAuthenticated = false, onApplyClick }: JobCardProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  };

  const isDeadlineSoon = () => {
    const deadline = new Date(job.deadline);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const handleApplyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      onApplyClick?.(job.id);
      return;
    }

    // If authenticated and has external application URL
    if (job.applicationUrl) {
      window.open(job.applicationUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Handle internal application (would redirect to job detail page)
      window.location.href = `/job-detail?id=${job.id}#apply`;
    }
  };

  return (
    <div className={`bg-card border rounded-industrial p-6 industrial-shadow hover:industrial-shadow-lg transition-industrial relative h-full flex flex-col ${
      job.isFeatured ? 'border-primary bg-gradient-to-r from-primary/5 to-transparent' : 'border-border'
    }`}>
      {/* Registration Required Badge */}
      {!isAuthenticated && (
        <div className="absolute top-4 right-4">
          <span className="bg-secondary text-secondary-foreground px-2 py-1 rounded text-xs font-heading font-bold uppercase tracking-wide">
            Register to Apply
          </span>
        </div>
      )}

      {/* Featured Badge */}
      {job.isFeatured && (
        <div className="flex items-center space-x-2 mb-4">
          <div className="bg-primary text-primary-foreground px-3 py-1 rounded-industrial text-xs font-heading font-bold uppercase tracking-wide">
            <Icon name="StarIcon" size={12} className="inline mr-1" />
            Featured Job
          </div>
          {job.isUrgent && (
            <div className="bg-error text-error-foreground px-3 py-1 rounded-industrial text-xs font-heading font-bold uppercase tracking-wide">
              <Icon name="ExclamationTriangleIcon" size={12} className="inline mr-1" />
              Urgent
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col flex-1">
        {/* Company Logo and Title Section */}
        <div className="flex items-start space-x-4 mb-4">
          {/* Company Logo */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-muted rounded-industrial overflow-hidden border border-border">
              <AppImage
                src={job.companyLogo}
                alt={job.companyLogoAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Job Title and Company */}
          <div className="flex-1 min-w-0">
            <Link
              href={`/job-detail?id=${job.id}`}
              className="block group"
            >
              <h3 className="font-heading text-base font-bold text-foreground group-hover:text-primary transition-industrial line-clamp-2 leading-tight">
                {job.title}
              </h3>
            </Link>
            <p className="text-text-secondary font-medium mt-1 text-sm">{job.company}</p>
          </div>
        </div>

        {/* Job Meta Information */}
        <div className="grid grid-cols-1 gap-2 text-sm text-text-secondary mb-3">
          <div className="flex items-center space-x-1">
            <Icon name="MapPinIcon" size={14} />
            <span className="truncate">{job.location}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="CurrencyDollarIcon" size={14} />
            <span className="font-medium text-foreground text-xs">{job.salary}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <Icon name="ClockIcon" size={14} />
              <span className="text-xs">{job.employmentType}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="AcademicCapIcon" size={14} />
              <span className="text-xs">{job.experienceLevel}</span>
            </div>
          </div>
        </div>

        {/* Job Description Preview */}
        <p className="text-text-secondary text-sm line-clamp-3 mb-4 flex-1">
          {job.description}
        </p>

        {/* Job Footer */}
        <div className="mt-auto">
          <div className="flex items-center justify-between mb-3 text-xs text-text-secondary">
            <span>Posted {formatDate(job.postedDate)}</span>
            {isDeadlineSoon() && (
              <span className="text-warning font-medium">
                <Icon name="ExclamationTriangleIcon" size={12} className="inline mr-1" />
                {new Date(job.deadline).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <Link
              href={`/job-detail?id=${job.id}`}
              className="w-full text-center px-3 py-2 border border-border rounded-industrial hover:bg-muted transition-industrial font-heading text-xs uppercase tracking-wide"
            >
              View Details
            </Link>
            <button
              onClick={handleApplyClick}
              className={`w-full px-3 py-2 rounded-industrial font-heading text-xs font-bold uppercase tracking-wide transition-industrial ${
                isAuthenticated 
                  ? 'bg-primary text-primary-foreground hover:bg-accent' 
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              {isAuthenticated ? 'Apply Now' : 'Register to Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCard;